/**
 * Club state and every mutation in the app.
 *
 * Actions here mirror the API surface in CLAUDE.md §4 one-for-one. Screens
 * never mutate `db` and never call `fetch` — they call an action.
 *
 * HOW A MUTATION WORKS NOW. Every action awaits the API, then applies a
 * precise local patch from the response:
 *
 *     await api.recordPayment(...)   →   { receipt, coversTill }
 *     patch(d => { push the payment; advance the member's dues date })
 *
 * The previous version did the opposite — it mutated a local draft, derived
 * receipt numbers and bibs in the browser, and saved the entire club back to
 * localStorage as one JSON blob. That could not survive contact with a real
 * database: two admins recording payments at the same moment would both mint
 * `TRC/2026-27/0007`. Now the server allocates anything that must be unique
 * inside a transaction, and this file's job is only to keep the screen in step
 * with what the server said.
 *
 * Where a write cascades across several collections — activating a member,
 * marking a duty done, deleting an event — the patch would have to guess at
 * ids and recomputed stats. Those call `refresh()` and take the club again.
 * One extra round trip beats a screen that disagrees with the database.
 *
 * Two invariants this file no longer has to enforce, because the API does, in
 * a transaction: bib and receipt allocation, and never hard-deleting a member.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { nextPaidTill, today } from '../data';
import { api, uploadImage } from '../lib/api';
import { useAuth } from './AuthContext';

/*
 * Role — which portal the session is in: 'member' | 'admin'. It comes from the
 * API, which reads it off the member record, and is never decided here.
 *
 * MemberDraft — fields an admin fills on the add/edit member form:
 *   name, phone, email, tier, dob, gender ('M'|'F'|'X'|null), ground, blood,
 *   tshirt, emName, emPhone, emRel?, medical?, guardianName, guardianPhone.
 *
 * JoinRequestDraft — the public join form:
 *   name, phone, email, dob, gender, ground, tier, blood, emName, emPhone,
 *   guardianName, guardianPhone, note.
 *
 * `phone` and `email` are BOTH login identities and both are mandatory: the
 * email is the only channel a forgotten password can be recovered through.
 */

// Context value: { db, loading, waking, loadError, role, meId, me, signedIn, signIn,
// signOut, refresh, and one action per API endpoint — see the useMemo below. }
const ClubContext = createContext(null);

export function ClubProvider({ children }) {
  const auth = useAuth();
  const [db, setDb] = useState(null);
  const [loading, setLoading] = useState(true);
  /*
   * True once the boot has been waiting on the API long enough that a member
   * deserves to be told why. The API runs on Render's free instance, which
   * spins down after ~15 minutes of quiet and takes ~50 seconds to wake
   * (render.yaml). A bare "Loading your club…" sitting there for most of a
   * minute is indistinguishable from a broken app — this is what lets the gate
   * say what is actually happening.
   */
  const [waking, setWaking] = useState(false);
  const [loadError, setLoadError] = useState(null);

  /** Latest db without re-creating every action on each render. */
  const dbRef = useRef(null);
  dbRef.current = db;

  /* ---------------- the session ----------------
   *
   * AuthContext owns WHO is signed in and WHICH club is active; this provider
   * owns the club's data. When the selection changes — sign-in, the club
   * switcher, a cold start restoring the stored choice — the club loads here.
   * Role and identity come back WITH the club payload (db.club.role,
   * db.club.membershipId), because they are per-club now, not per-account.
   */
  const signedIn = auth.signedIn;
  const role = db?.club?.role === 'admin' ? 'admin' : (db?.club?.role ?? 'member');
  const meId = db?.club?.membershipId ?? null;

  useEffect(() => {
    let cancelled = false;
    if (auth.booting) return undefined;
    if (!auth.signedIn || !auth.activeClubId) {
      setDb(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setLoadError(null);
    const slow = setTimeout(() => {
      if (!cancelled) setWaking(true);
    }, 2500);
    api
      .loadClub()
      .then((club) => {
        if (cancelled) return;
        dbRef.current = club;
        setDb(club);
      })
      .catch((err) => {
        if (cancelled) return;
        setDb(null);
        setLoadError(err?.message ?? 'Could not load the club.');
      })
      .finally(() => {
        clearTimeout(slow);
        if (!cancelled) {
          setWaking(false);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
      clearTimeout(slow);
    };
  }, [auth.booting, auth.signedIn, auth.activeClubId]);

  /**
   * One field takes a mobile number OR an email; the server resolves which.
   * Returns the LANDING — SignIn routes on it: 'club' goes straight to the
   * portal, 'select' opens the club selector (several clubs, an owned portal,
   * or the platform console).
   */
  const signIn = useCallback(
    async (identifier, password) => {
      const { session, landing } = await auth.signIn(identifier, password);
      return { landing, name: session.user.name, isSuperAdmin: session.user.isSuperAdmin };
    },
    [auth],
  );

  const signOut = useCallback(async () => {
    auth.signOut();
    setDb(null);
  }, [auth]);

  /* ---------------- local state plumbing ---------------- */

  /**
   * Applies a local patch. The server already has the change — this only keeps
   * the rendered club in step, so it never fails and never persists.
   */
  const patch = useCallback((mutate) => {
    const current = dbRef.current;
    if (!current) return;
    // Structured clone keeps the mutator honest: no accidental shared refs
    // back into the rendered state.
    const draft = structuredClone(current);
    mutate(draft);
    dbRef.current = draft;
    setDb(draft);
  }, []);

  /** Takes the whole club again. For writes that cascade across collections. */
  const refresh = useCallback(async () => {
    const club = await api.loadClub();
    dbRef.current = club;
    setDb(club);
    return club;
  }, []);

  const me = useMemo(
    () => (meId ? (db?.members.find((m) => m.id === meId) ?? null) : null),
    [db, meId],
  );

  /* ---------------- members ---------------- */

  const addMember = useCallback(
    async (draft) => {
      const member = await api.addMember(draft);
      patch((d) => {
        d.members.push(member);
      });
      return member.id;
    },
    [patch],
  );

  const updateMember = useCallback(
    async (id, changes) => {
      // A member editing themselves goes through /me, which allows a narrower
      // set of fields; the committee's own edits go through /members/:id.
      const member = id === meId && role !== 'admin'
        ? await api.updateMe(changes)
        : await api.updateMember(id, changes);

      patch((d) => {
        const i = d.members.findIndex((x) => x.id === id);
        if (i >= 0) d.members[i] = { ...d.members[i], ...member };
      });
      return member;
    },
    [patch, meId, role],
  );

  /**
   * Sets or changes the signed-in member's own password.
   *
   * `current` is required only when they already have one — the members who
   * need this most are the ones the committee added by hand, who never had one.
   */
  const setOwnPassword = useCallback(
    async (memberId, password, current = null) => {
      await api.setOwnPassword(current, password);
      patch((d) => {
        const m = d.members.find((x) => x.id === memberId);
        if (m) {
          m.hasPassword = true;
          // Chosen by the member, so the committee-generated one (if that is
          // what they signed in with) stops nagging.
          m.mustChangePassword = false;
        }
      });
    },
    [patch],
  );

  /**
   * The photo is resized on the canvas first (lib/image.js), uploaded to
   * Firebase Storage here, and only its URL is stored. A base64 image in a
   * Firestore document is paid for on every read of it, forever.
   */
  const setPhoto = useCallback(
    async (id, photo) => {
      const url = photo ? await uploadImage(photo) : null;
      await api.setPhoto(url);
      patch((d) => {
        const m = d.members.find((x) => x.id === id);
        if (m) m.photo = url;
      });
      return url;
    },
    [patch],
  );

  /** Never a hard delete — bib numbers are never reused. SPEC.md §4. */
  const removeMember = useCallback(
    async (id) => {
      await api.removeMember(id);
      patch((d) => {
        const m = d.members.find((x) => x.id === id);
        if (m) m.status = 'left';
      });
    },
    [patch],
  );

  /**
   * The committee's final approval for a member they added by hand — flips
   * `pending` to active so they appear on the roster and can sign in. The
   * server refuses it until a joining payment is on the book, and returns the
   * member as stored (activation also stamps `joined`).
   */
  const activateMember = useCallback(
    async (id) => {
      const member = await api.activateMember(id);
      patch((d) => {
        const i = d.members.findIndex((x) => x.id === id);
        if (i >= 0) d.members[i] = { ...d.members[i], ...member };
      });
      return member;
    },
    [patch],
  );

  /* ---------------- money ---------------- */

  /**
   * The receipt number and the new expiry both come back from the server,
   * which computed them in a transaction. Nothing about money is derived here.
   */
  const recordPayment = useCallback(
    async (memberId, amount, method, paidOn) => {
      const { paymentId, receipt, coversTill } = await api.recordPayment(
        memberId,
        amount,
        method,
        paidOn,
      );

      patch((d) => {
        d.payments.push({
          id: paymentId,
          memberId,
          amount,
          method,
          date: paidOn,
          coversTill,
          receipt,
        });
        const m = d.members.find((x) => x.id === memberId);
        if (m) {
          if (coversTill) m.duesPaidTill = coversTill;
          m.linkSent = false;
          m.linkSentOn = null;
        }
      });

      return receipt;
    },
    [patch],
  );

  /**
   * A member paying their own dues.
   *
   * Separate from `recordPayment`, which is the committee entering money that
   * arrived by cash or UPI and is admin-only. This one takes no amount and no
   * date: the server uses their tier's fee and today.
   */
  const payOwnDues = useCallback(async (proof) => {
    // The server decides the amount (the CLUB's joining or renewal fee) and
    // reports what it charged — the local record repeats it, never guesses.
    const { paymentId, receipt, coversTill, amount } = await api.payOwnDues(proof);

    patch((d) => {
      const m = d.members.find((x) => x.id === meId);
      if (!m) return;
      d.payments.push({
        id: paymentId,
        memberId: meId,
        amount,
        method: 'razorpay',
        date: today(),
        coversTill,
        receipt,
      });
      if (coversTill) m.duesPaidTill = coversTill;
      m.linkSent = false;
      m.linkSentOn = null;
    });

    return receipt;
  }, [patch, meId]);

  /**
   * Re-sends the set-password email to an ACTIVE member who has not chosen a
   * password yet (owner's direction, 2 Oct 2026 — the committee no longer
   * generates passwords; the member always sets their own from the email).
   */
  const resendSetPasswordEmail = useCallback(async (memberId) => {
    await api.resendInvite(memberId);
  }, []);

  /**
   * The home grounds on offer — the CLUB's own list, and nothing else.
   *
   * The three Tirupur grounds used to be platform constants spread in front
   * of this map, which on a multi-tenant platform meant a Chennai club was
   * offered "Nanjappa School Grounds" and could not remove it. The committee
   * names its grounds under Settings now (owner's direction, 2 Sep 2026) and
   * every form offers exactly those.
   */
  // `db?` and not `db.` — the club is null until the first load resolves, which
  // is the entire time the sign-in screen is on screen. Reading db.meta there
  // threw and took the whole provider down with it.
  const grounds = useMemo(() => db?.meta?.grounds ?? {}, [db?.meta]);

  const addGround = useCallback(
    async (name) => {
      const created = await api.addGround(name);
      patch((d) => {
        d.meta.grounds = { ...(d.meta.grounds ?? {}), [created.id]: created.name };
      });
      return created;
    },
    [patch],
  );

  const removeGround = useCallback(
    async (id) => {
      await api.removeGround(id);
      patch((d) => {
        const next = { ...(d.meta.grounds ?? {}) };
        delete next[id];
        d.meta.grounds = next;
      });
    },
    [patch],
  );

  /** Creates the payment link and marks the member chased. */
  const markLinkSent = useCallback(
    async (memberId) => {
      const link = await api.sendPayLink(memberId);
      patch((d) => {
        const m = d.members.find((x) => x.id === memberId);
        if (m) {
          m.linkSent = true;
          m.linkSentOn = today();
        }
      });
      return link;
    },
    [patch],
  );

  /* ---------------- results ---------------- */

  /**
   * The server recomputes the member's stats from the FULL result set and
   * returns them, so personal bests, medals and the bib colour cannot drift.
   * CLAUDE.md §10.10.
   */
  const addResult = useCallback(
    async (input) => {
      const { id, stats } = await api.addResult(input);
      patch((d) => {
        d.results.push({ id, ...input });
        const m = d.members.find((x) => x.id === input.memberId);
        if (m) m.stats = stats;
      });
      return id;
    },
    [patch],
  );

  /* ---------------- events ---------------- */

  /**
   * `image` may arrive as a resized JPEG data URL from lib/image.js. It goes
   * to Storage FIRST and only the URL is sent — the API refuses a data URL
   * outright, for the same reason chat does (CLAUDE.md §12): a base64 banner
   * in the event document would be paid for on every events read, forever.
   */
  const addEvent = useCallback(
    async (input) => {
      const { image, ...rest } = input;
      const url = image ? await uploadImage(image) : null;
      const payload = { ...rest, image: url };

      const { id } = await api.addEvent(payload);
      patch((d) => {
        d.events.push({ id, rsvp: [], ...payload });
      });
      return id;
    },
    [patch],
  );

  /**
   * Mark an event finished, or undo it. This is the ONLY thing that moves the
   * club's total distance — see `clubDistanceKm` in data/stats.js.
   */
  const setEventCompleted = useCallback(
    async (id, completed) => {
      const { completedAt } = await api.setEventCompleted(id, completed);
      patch((d) => {
        const e = d.events.find((x) => x.id === id);
        if (e) e.completedAt = completedAt ?? null;
      });
    },
    [patch],
  );

  /**
   * Put a member in charge of an event, or clear it (`headId: null`). The head
   * gains a tab in their own rail and may log results and volunteer hours for
   * this event until the committee marks it completed — see data/lead.js.
   */
  const setEventHead = useCallback(
    async (id, headId) => {
      const res = await api.setEventHead(id, headId);
      patch((d) => {
        const e = d.events.find((x) => x.id === id);
        if (e) e.headId = res.headId ?? null;
      });
    },
    [patch],
  );

  /**
   * The event's own web page — a race site, a registration form. Its own
   * action rather than a field on an edit form, because there is no event
   * edit: the address usually turns up after the event is already posted.
   */
  const setEventLink = useCallback(
    async (id, linkUrl) => {
      const res = await api.setEventLink(id, linkUrl);
      patch((d) => {
        const e = d.events.find((x) => x.id === id);
        if (e) e.linkUrl = res.linkUrl ?? null;
      });
      return res.linkUrl ?? null;
    },
    [patch],
  );

  /**
   * The committee's typed totals for an event — registered and collected —
   * and whether members see them. Patched from the server's answer, so the
   * screen shows exactly what was stored.
   */
  const setEventFigures = useCallback(
    async (id, figures) => {
      const res = await api.setEventFigures(id, figures);
      patch((d) => {
        const e = d.events.find((x) => x.id === id);
        if (e) {
          e.registeredCount = res.registeredCount ?? null;
          e.amountCollected = res.amountCollected ?? null;
          e.figuresPublic = Boolean(res.figuresPublic);
        }
      });
    },
    [patch],
  );

  /** Cascades to departments and their sign-ups, so take the club again. */
  const removeEvent = useCallback(
    async (id) => {
      await api.removeEvent(id);
      await refresh();
    },
    [refresh],
  );

  const toggleRsvp = useCallback(
    async (eventId, going) => {
      await api.toggleRsvp(eventId, going);
      patch((d) => {
        const e = d.events.find((x) => x.id === eventId);
        if (!e || !meId) return;
        e.rsvp = going
          ? e.rsvp.includes(meId)
            ? e.rsvp
            : [...e.rsvp, meId]
          : e.rsvp.filter((x) => x !== meId);
        e.rsvpCount = e.rsvp.length;
      });
    },
    [patch, meId],
  );

  /* ---------------- volunteering ---------------- */

  const addDepartment = useCallback(
    async (input) => {
      const { eventId, ...rest } = input;
      const { id } = await api.addDepartment(eventId, rest);
      patch((d) => {
        d.departments.push({ id, eventId, ...rest });
      });
      return id;
    },
    [patch],
  );

  /** The API needs the event too — a department is a subcollection of one. */
  const removeDepartment = useCallback(
    async (deptId) => {
      const dept = dbRef.current?.departments.find((x) => x.id === deptId);
      if (!dept) throw new Error('That department is no longer on the event.');

      await api.removeDepartment(dept.eventId, deptId);
      patch((d) => {
        d.departments = d.departments.filter((x) => x.id !== deptId);
        d.signups = d.signups.filter((s) => s.deptId !== deptId);
      });
    },
    [patch],
  );

  const joinDepartment = useCallback(
    async (deptId) => {
      const dept = dbRef.current?.departments.find((x) => x.id === deptId);
      if (!dept) throw new Error('That department is no longer on the event.');

      // The capacity check is the server's — the UI hides a full team's button,
      // but two phones can tap it at the same moment.
      const signup = await api.joinDepartment(dept.eventId, deptId);
      patch((d) => {
        d.signups.push({ id: signup.id, deptId, memberId: meId, status: signup.status });
      });
    },
    [patch, meId],
  );

  const leaveDepartment = useCallback(
    async (deptId) => {
      const dept = dbRef.current?.departments.find((x) => x.id === deptId);
      if (!dept) throw new Error('That department is no longer on the event.');

      await api.leaveDepartment(dept.eventId, deptId);
      patch((d) => {
        d.signups = d.signups.filter((s) => !(s.deptId === deptId && s.memberId === meId));
      });
    },
    [patch, meId],
  );

  const confirmSignup = useCallback(
    async (signupId) => {
      await api.confirmSignup(signupId);
      patch((d) => {
        const s = d.signups.find((x) => x.id === signupId);
        if (s) s.status = 'confirmed';
      });
    },
    [patch],
  );

  /**
   * Marking a duty done is the only path that credits hours automatically: the
   * server writes a verified volunteerLog row, clears the sign-up and
   * recomputes the member's stats. Three collections change, so refresh.
   * SPEC.md §6.2.
   */
  const completeSignup = useCallback(
    async (signupId) => {
      const { hours } = await api.completeSignup(signupId);
      await refresh();
      return hours;
    },
    [refresh],
  );

  /** A manual entry starts unverified — an admin has to confirm it. */
  const logVolunteerHours = useCallback(
    async (input) => {
      const { id } = await api.logVolunteerHours(input);
      patch((d) => {
        // verified: the API writes these already verified (committee and event
        // heads are trusted entries), so claiming otherwise here would show a
        // row as pending until the next full load.
        d.volunteer.push({ id, eventId: null, verified: true, ...input });
      });
      return id;
    },
    [patch],
  );

  /** Verifying changes the member's hours total, and so their medals. */
  const verifyHours = useCallback(
    async (logId) => {
      await api.verifyHours(logId);
      await refresh();
    },
    [refresh],
  );

  /* ---------------- the join pipeline ----------------
   *
   *   submitJoinRequest  a stranger asks              → pending
   *   sendJoinLink       committee approves & sends    → link_sent
   *   payJoin            they pay on the link          → paid
   *   completeJoin       they set a password           → link SPENT
   *   activateRequest    the committee's FINAL approval → the member exists
   *
   * The three middle steps are PUBLIC and run without a session, from
   * /join/:token. They do not touch `db` — the visitor has no club loaded.
   */

  const submitJoinRequest = useCallback(
    async (draft) => {
      const { id } = await api.submitJoinRequest(draft);
      // Only the committee has a club loaded; a stranger on the sign-in screen
      // has none, and there is nothing to patch.
      patch((d) => {
        d.requests.push({ ...draft, id, status: 'pending', ts: Date.now() });
      });
      return id;
    },
    [patch],
  );

  const sendJoinLink = useCallback(
    async (requestId) => {
      const { token } = await api.sendJoinLink(requestId);
      patch((d) => {
        const r = d.requests.find((x) => x.id === requestId);
        if (r) {
          r.status = 'link_sent';
          r.linkSentOn = today();
        }
      });
      return token;
    },
    [patch],
  );

  /** PUBLIC. What /join/:token renders. Throws LINK_DEAD when the link is spent. */
  const openJoinLink = useCallback((token) => api.openJoinLink(token), []);

  /** PUBLIC. → the receipt number, allocated server-side. */
  const payJoin = useCallback(async (token, proof) => {
    const { receipt } = await api.payJoinLink(token, proof);
    return receipt;
  }, []);

  /** PUBLIC. Confirms details, sets the password, spends the link. */
  const completeJoin = useCallback((token, payload) => api.onboardJoin(token, payload), []);

  /**
   * The joining fee, handed over instead of tapped. Marks the request paid
   * with a real receipt number, which is what makes Activate available — the
   * member's link stays live so they can still set their own password.
   */
  const recordJoinCash = useCallback(
    async (requestId, draft) => {
      const { receipt, amount, paidOn, method } = await api.recordJoinCash(requestId, draft);
      patch((d) => {
        const r = d.requests.find((x) => x.id === requestId);
        if (r) {
          r.status = 'paid';
          r.receipt = receipt;
          r.amount = amount;
          r.paidOn = paidOn;
          r.method = method;
        }
      });
      return receipt;
    },
    [patch],
  );

  /**
   * The committee's final approval. Creates the member, allocates the bib and
   * moves the joining payment onto the roster — so take the club again.
   */
  const activateRequest = useCallback(
    async (requestId) => {
      const result = await api.activateRequest(requestId);
      await refresh();
      return result;
    },
    [refresh],
  );

  /** Declined requests move to Handled — they never disappear. */
  const declineRequest = useCallback(
    async (id, reason) => {
      await api.declineRequest(id, reason);
      patch((d) => {
        const r = d.requests.find((x) => x.id === id);
        if (r) {
          r.status = 'declined';
          r.declineReason = reason;
        }
      });
    },
    [patch],
  );

  /* ---------------- forgotten passwords ----------------
   *
   * Both PUBLIC and unauthenticated by necessity — the member cannot sign in,
   * that is the problem. Member and committee accounts share one path: a
   * locked-out admin has exactly the same problem as a locked-out runner, and a
   * second code path is a second place to get it wrong.
   */

  /**
   * Step 1. They type a mobile number OR an email; the server finds the account
   * behind it and emails a one-time link to the address ON THE RECORD — never
   * to whatever was typed. That is the point of accepting a number: they may
   * not remember which address the club has, so it is looked up and shown back
   * masked.
   *
   * → { sent, maskedEmail, ttlMinutes }. It never returns the link.
   */
  const requestPasswordReset = useCallback((identifier) => api.forgotPassword(identifier), []);

  /**
   * Step 2. The link is opened and a new password chosen. Spends the link.
   * Deliberately does NOT sign them in: they go back to the sign-in screen and
   * use the password, which is the one thing that proves it took.
   */
  const resetPasswordWithToken = useCallback(
    async (token, password) => {
      await api.resetPassword(token, password);
      return null;
    },
    [],
  );

  /* ---------------- messages ---------------- */

  const sendNotice = useCallback(
    async (text) => {
      const { id } = await api.sendNotice(text);
      patch((d) => {
        d.messages.push({
          id,
          kind: 'notice',
          from: 'admin',
          to: 'all',
          text,
          ts: Date.now(),
          readBy: [],
          replies: [],
        });
      });
      return id;
    },
    [patch],
  );

  /**
   * The same notice, addressed to one member.
   *
   * Nothing new is needed on the read side: the Notices tab, the bell count and
   * markNoticesRead all already match on `to === 'all' || to === meId`.
   */
  const sendMemberNotice = useCallback(
    async (memberId, text) => {
      const { id } = await api.sendMemberNotice(memberId, text);
      patch((d) => {
        d.messages.push({
          id,
          kind: 'notice',
          from: 'admin',
          to: memberId,
          text,
          ts: Date.now(),
          readBy: [],
          replies: [],
        });
      });
      return id;
    },
    [patch],
  );

  const sendSuggestion = useCallback(
    async (text) => {
      const { id } = await api.sendSuggestion(text);
      patch((d) => {
        d.messages.push({
          id,
          kind: 'suggestion',
          from: meId,
          to: 'admin',
          text,
          ts: Date.now(),
          readBy: [],
          replies: [],
        });
      });
      return id;
    },
    [patch, meId],
  );

  const replyToMessage = useCallback(
    async (messageId, text) => {
      await api.replyToMessage(messageId, text);
      patch((d) => {
        const m = d.messages.find((x) => x.id === messageId);
        if (m) m.replies = [...(m.replies ?? []), { text, ts: Date.now() }];
      });
    },
    [patch],
  );

  const markNoticesRead = useCallback(async () => {
    await api.markNoticesRead();
    patch((d) => {
      if (!meId) return;
      for (const m of d.messages) {
        if ((m.to === 'all' || m.to === meId) && !m.readBy.includes(meId)) {
          m.readBy.push(meId);
        }
      }
    });
  }, [patch, meId]);

  /* ---------------- chat ---------------- */

  /**
   * Text only. Attaching an image was retired on 27 Aug 2026 (owner's
   * direction) — older messages that carry one still render, nothing new can
   * add one.
   *
   * Mentions are resolved by the SERVER from the roster, not sent from here: a
   * client-supplied mention list would let a member fabricate a mention of
   * somebody they never named, and a mention is a notification.
   */
  const sendChat = useCallback(
    async (text) => {
      const { id, mentions } = await api.sendChat(text);

      patch((d) => {
        const author = d.members.find((x) => x.id === meId);
        d.chat.push({
          id,
          from: role === 'admin' ? 'admin' : (meId ?? 'admin'),
          name: role === 'admin' ? 'Committee' : (author?.name ?? 'Member'),
          admin: role === 'admin',
          text,
          image: null,
          ts: Date.now(),
          // The count is authoritative; which members were tagged is only
          // needed for highlighting, which the next load brings.
          mentions: [],
        });
        d.meta.chatSeen = Date.now();
      });

      return mentions;
    },
    [patch, meId, role],
  );

  const markChatSeen = useCallback(async () => {
    const { chatSeen } = await api.markChatSeen();
    patch((d) => {
      d.meta.chatSeen = chatSeen;
    });
  }, [patch]);

  /* ---------------- club settings ---------------- */

  const setSafetyContact = useCallback(
    async (name, phone) => {
      const meta = await api.setSafetyContact(name, phone);
      patch((d) => {
        d.meta.sosName = meta.sosName;
        d.meta.sosPhone = meta.sosPhone;
      });
    },
    [patch],
  );

  /**
   * The bank / UPI block and the QR every public payment link prints. The QR
   * arrives as a resized data URL and is uploaded here, so only a URL is ever
   * stored — same rule as a member photo.
   */
  const setPaymentDetails = useCallback(
    async ({ details, upi, qr, payMode, bankHolder, bankAccount, bankIfsc, rpKeyId, rpKeySecret }) => {
      const qrUrl = qr && qr.startsWith('data:') ? await uploadImage(qr) : (qr ?? null);
      const meta = await api.setPaymentDetails({
        details, upi, qrUrl, payMode, bankHolder, bankAccount, bankIfsc, rpKeyId, rpKeySecret,
      });
      patch((d) => {
        d.meta.bankDetails = meta.bankDetails;
        d.meta.bankUpi = meta.bankUpi;
        d.meta.bankQrUrl = meta.bankQrUrl;
        d.meta.payMode = meta.payMode;
        d.meta.bankHolder = meta.bankHolder;
        d.meta.bankAccount = meta.bankAccount;
        d.meta.bankIfsc = meta.bankIfsc;
        d.meta.rpKeyId = meta.rpKeyId;
        d.meta.rpConfigured = meta.rpConfigured;
      });
    },
    [patch],
  );

  /**
   * The club's joining and renewal amounts, per tier. The server validates,
   * resolves and echoes the complete map; links already issued keep the
   * amount they were issued with — a link is a quote.
   */
  const setFees = useCallback(
    async (fees) => {
      const saved = await api.setFees(fees);
      patch((d) => {
        d.meta.fees = saved.fees;
      });
    },
    [patch],
  );

  const value = useMemo(
    () => ({
      db,
      loading,
      waking,
      loadError,
      role,
      meId,
      me,
      signIn,
      signOut,
      signedIn,
      refresh,
      addMember,
      updateMember,
      setOwnPassword,
      requestPasswordReset,
      resetPasswordWithToken,
      setPhoto,
      removeMember,
      activateMember,
      recordPayment,
      payOwnDues,
      markLinkSent,
      resendSetPasswordEmail,
      grounds,
      addGround,
      removeGround,
      addResult,
      addEvent,
      removeEvent,
      setEventCompleted,
      setEventHead,
      setEventLink,
      setEventFigures,
      toggleRsvp,
      addDepartment,
      removeDepartment,
      joinDepartment,
      leaveDepartment,
      confirmSignup,
      completeSignup,
      logVolunteerHours,
      verifyHours,
      submitJoinRequest,
      sendJoinLink,
      recordJoinCash,
      openJoinLink,
      payJoin,
      completeJoin,
      activateRequest,
      declineRequest,
      sendNotice,
      sendMemberNotice,
      sendSuggestion,
      replyToMessage,
      markNoticesRead,
      sendChat,
      markChatSeen,
      setSafetyContact,
      setPaymentDetails,
      setFees,
    }),
    [
      db,
      loading,
      waking,
      loadError,
      role,
      meId,
      me,
      signIn,
      signOut,
      signedIn,
      refresh,
      addMember,
      updateMember,
      setOwnPassword,
      requestPasswordReset,
      resetPasswordWithToken,
      setPhoto,
      removeMember,
      activateMember,
      recordPayment,
      payOwnDues,
      markLinkSent,
      resendSetPasswordEmail,
      grounds,
      addGround,
      removeGround,
      addResult,
      addEvent,
      removeEvent,
      setEventCompleted,
      setEventHead,
      setEventLink,
      setEventFigures,
      toggleRsvp,
      addDepartment,
      removeDepartment,
      joinDepartment,
      leaveDepartment,
      confirmSignup,
      completeSignup,
      logVolunteerHours,
      verifyHours,
      submitJoinRequest,
      sendJoinLink,
      recordJoinCash,
      openJoinLink,
      payJoin,
      completeJoin,
      activateRequest,
      declineRequest,
      sendNotice,
      sendMemberNotice,
      sendSuggestion,
      replyToMessage,
      markNoticesRead,
      sendChat,
      markChatSeen,
      setSafetyContact,
      setPaymentDetails,
      setFees,
    ],
  );

  return <ClubContext.Provider value={value}>{children}</ClubContext.Provider>;
}

export function useClub() {
  const ctx = useContext(ClubContext);
  if (!ctx) throw new Error('useClub must be used inside <ClubProvider>');
  return ctx;
}

/**
 * The club with data loaded. Screens render inside <AppShell>, which does not
 * mount until loading finishes, so they can rely on this.
 */
export function useClubData() {
  const ctx = useClub();
  if (!ctx.db) throw new Error('Club data is not loaded yet');
  return ctx;
}

/**
 * Membership runs 12 months from today, or from the existing expiry.
 *
 * A PREVIEW, so the committee can see what they are about to grant before they
 * grant it. The authoritative date is computed server-side, inside the payment
 * transaction, by `nextPaidTill` — the same function this calls. They cannot
 * disagree, and this one never writes anything.
 */
export function previewCoversTill(member, paidOn = today()) {
  return nextPaidTill(member.duesPaidTill, paidOn, member.tier);
}

/**
 * The home grounds map, for any screen behind sign-in.
 *
 * Separate from useClub() only so a component that wants nothing else does not
 * have to destructure the whole club to render one label.
 */
export function useGrounds() {
  return useClub().grounds;
}

/**
 * The club's fee map (joining + renewal per tier), already resolved by the
 * server. Null until the club loads — every reader passes it through
 * memberFee/feeFor, which treat null as the platform defaults.
 */
export function useFees() {
  return useClub().db?.meta?.fees ?? null;
}
